import {
  FILE_READ_ERROR_MESSAGE,
  isFileReadError,
  messageForFailure,
  NETWORK_ERROR_MESSAGE,
  UNEXPECTED_ERROR_MESSAGE,
} from './messages';
import type { ProblemDetails } from './types';

// All data comes from the Fastify `/api/v1` contract (ADR-0003/0004). The
// session is an httpOnly cookie set by POST /auth/login, so every call is
// credentialed; the client never sees or stores the token (the browser
// attaches it). In dev, Vite proxies /api to the local API so this is
// same-origin; in prod each Pages surface is configured with its API origin.
const BASE = '/api/v1';

/** What a person sees when a READ is still refused as rate-limited after the
 * retries below (a busy room, not a mistake of theirs). */
export const SERVER_BUSY_MESSAGE = 'The server is busy — this will refresh shortly.';
/** A rate-limited GET waits for Retry-After (default / cap, seconds) and retries
 * this many times before surfacing an error. Never applied to non-GET. */
const GET_429_RETRIES = 3;
const GET_429_DEFAULT_WAIT_S = 2;
const GET_429_MAX_WAIT_S = 10;

export class ApiError extends Error {
  /** HTTP status, or 0 when no response arrived (network failure). */
  readonly status: number;
  readonly problem: ProblemDetails | undefined;
  /** The original technical detail (raw non-JSON body excerpt, network error
   * text) for debugging. NEVER display it — `message` is the user-facing text. */
  readonly debug: string | undefined;
  /** How long the server asked the caller to wait before trying again (its
   * `Retry-After` header, else the problem body's `retryAfter`), in seconds. */
  readonly retryAfterSeconds: number | undefined;

  constructor(
    status: number,
    message: string,
    problem?: ProblemDetails,
    debug?: string,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.debug = debug;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** A failed response's wait hint in seconds: Retry-After (delta-seconds), else
 * the problem body's numeric `retryAfter`; undefined when neither is usable. */
function retryAfterOf(res: Response, problem: ProblemDetails | undefined): number | undefined {
  const header = Number(res.headers.get('retry-after') ?? NaN);
  if (Number.isFinite(header) && header >= 0) return header;
  const body = (problem as { retryAfter?: unknown } | undefined)?.retryAfter;
  return typeof body === 'number' && Number.isFinite(body) && body >= 0 ? body : undefined;
}

/** A fetch cancelled by its AbortSignal (react-query cancellation, unmount):
 * rethrown untouched so callers can tell it apart from a failure. */
function isAbort(err: unknown): boolean {
  return err instanceof DOMException
    ? err.name === 'AbortError'
    : (err as { name?: string } | null)?.name === 'AbortError';
}

/**
 * Every failure leaves this function as an {@link ApiError} with a message fit
 * for a person: a network drop (status 0), an HTML 502/524 page from the edge, a
 * 5xx, and a non-JSON success body all used to surface as "Failed to fetch" or a
 * raw `SyntaxError: Unexpected token '<'`. The technical cause is kept on
 * `debug` (and the problem body on `problem`), never in `message`.
 */
async function request<T>(path: string, init?: RequestInit, attempt = 0): Promise<T> {
  // Only advertise a JSON body when there actually is one. A parameterless POST
  // (e.g. seed template, release phase, revoke grant) sends no
  // body, and Fastify rejects `Content-Type: application/json` with an empty
  // body as 400 "Body cannot be empty" — which silently broke every such button.
  const hasBody = init?.body != null;
  let res: Response;
  let text: string;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...(init?.headers ?? {}),
      },
    });
    text = await res.text();
  } catch (err) {
    if (isAbort(err)) throw err;
    throw new ApiError(0, NETWORK_ERROR_MESSAGE, undefined, String(err));
  }

  let data: unknown;
  let isJson = true;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      isJson = false;
    }
  }

  if (!res.ok) {
    const problem =
      isJson && data !== null && typeof data === 'object' ? (data as ProblemDetails) : undefined;
    // A rate-limited READ is safe to repeat: wait as asked, then try again. Never
    // for writes (no double-submits); their 429 wording is unchanged.
    const isGet = (init?.method ?? 'GET').toUpperCase() === 'GET';
    if (res.status === 429 && isGet) {
      if (attempt < GET_429_RETRIES) {
        const wait = Math.min(
          retryAfterOf(res, problem) ?? GET_429_DEFAULT_WAIT_S,
          GET_429_MAX_WAIT_S,
        );
        await new Promise((resolve) => setTimeout(resolve, wait * 1000));
        return request<T>(path, init, attempt + 1);
      }
      throw new ApiError(429, SERVER_BUSY_MESSAGE, problem, undefined, retryAfterOf(res, problem));
    }
    const retryAfter = retryAfterOf(res, problem);
    throw new ApiError(
      res.status,
      messageForFailure(res.status, problem, retryAfter),
      problem,
      isJson ? undefined : `HTTP ${res.status} non-JSON body: ${text.slice(0, 300)}`,
      retryAfter,
    );
  }
  if (!isJson) {
    throw new ApiError(
      res.status,
      UNEXPECTED_ERROR_MESSAGE,
      undefined,
      `HTTP ${res.status} non-JSON body: ${text.slice(0, 300)}`,
    );
  }

  return data as T;
}

/** The text to show a person for any error value: an ApiError's (already
 * friendly) message, a generic sentence for anything else, null for no error.
 * Never returns a raw technical message (SyntaxError, "Failed to fetch"). */
export function errorMessage(error: unknown): string | null {
  if (error == null || error === false) return null;
  if (error instanceof ApiError) return error.message;
  // Reading the picked file failed before anything was sent (found by the
  // full-deal test: a folder upload's file "failed" with "Something went wrong").
  if (isFileReadError(error)) return FILE_READ_ERROR_MESSAGE;
  return UNEXPECTED_ERROR_MESSAGE;
}

export const api = {
  /** `signal`: cancels the read (react-query's, or a timeout) — a read nothing else bounds. */
  get: <T>(path: string, options: { signal?: AbortSignal } = {}) =>
    request<T>(path, { method: 'GET', ...(options.signal ? { signal: options.signal } : {}) }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** POST a file's raw bytes (`application/octet-stream`), with `headers` — no
   * JSON, no base64. The upload panels send each file this way. */
  postBytes: <T>(path: string, bytes: Blob, headers: Record<string, string>) =>
    request<T>(path, {
      method: 'POST',
      body: bytes,
      headers: { ...headers, 'Content-Type': 'application/octet-stream' },
    }),
};
